import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CaseStudyCard } from "@/components/CaseStudyCard";
import { CASE_STUDIES, CONTACT } from "@/data/site";

const FILTERS = ["All", "AI Development", "n8n Automation", "React Full-Stack"];

export default function CaseStudies() {
  const [filter, setFilter] = useState("All");
  const visible = useMemo(
    () => (filter === "All" ? CASE_STUDIES : CASE_STUDIES.filter((s) => s.category === filter)),
    [filter]
  );

  return (
    <>
      <Seo
        title="Case Studies — codeRealm"
        siteName="codeRealm"
        description="In-depth explorations of AI systems, n8n automations and React full-stack platforms engineered by codeRealm World Services LLP."
      />
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        <MaskedLine>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Proof, not promises</span>
        </MaskedLine>
        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.12}>Case</MaskedLine>
          <MaskedLine delay={0.24}>
            <span className="text-outline">Studies</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.35} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Three engagements, three very different battlefields — one shared
            obsession with measurable outcomes.
          </p>
        </Reveal>

        <Reveal delay={0.45} className="mt-12 flex flex-wrap gap-3" y={16}>
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`filter-tab-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                filter === f
                  ? "border-coral bg-coral text-white"
                  : "border-border text-muted-foreground hover:border-coral/60 hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <div data-testid="case-studies-grid" className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((study, i) => (
            <Reveal key={study.id} delay={i * 0.08}>
              <CaseStudyCard study={study} index={i} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 rounded-3xl border border-border bg-card p-10 sm:p-14">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Your project could be chapter four.
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
                Send us the problem. We'll come back with a technical approach,
                a timeline and a number — usually within one working day.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="case-studies-cta-button"
              className="group flex w-fit items-center gap-2 rounded-full bg-coral px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
            >
              Get an evaluation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <p className="mt-8 border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70">
            Or write directly — {CONTACT.email}
          </p>
        </Reveal>
      </section>
    </>
  );
}
